import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import { TUTORIALS } from './steps';
import { TutorialContextValue, TutorialName } from './types';

const TutorialContext = createContext<TutorialContextValue | null>(null);

// Stable identity so the context value doesn't change on every render while no
// tutorial is running (an inline [] would break the useMemo below).
const NO_STEPS: TutorialContextValue['steps'] = [];

export const useTutorial = () => {
	const context = useContext(TutorialContext);
	if (!context) {
		throw new Error('useTutorial must be used within a TutorialProvider');
	}
	return context;
};

/**
 * Owns tutorial state for the whole app.
 *
 * Mounted in layouts/Global — above <Outlet /> — on purpose: the payroll
 * tutorial walks across three routes, and a provider that lived on a page would
 * be unmounted (taking the tour with it) the moment the tour navigated away.
 */
export const TutorialProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const [activeTutorial, setActiveTutorial] = useState<TutorialName | null>(
		null
	);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [completedTutorial, setCompletedTutorial] =
		useState<TutorialName | null>(null);

	const startTutorial = useCallback((tutorial: TutorialName) => {
		setCompletedTutorial(null);
		setActiveTutorial(tutorial);
		setCurrentStepIndex(0);
	}, []);

	const exitTutorial = useCallback(() => {
		setActiveTutorial(null);
		setCurrentStepIndex(0);
	}, []);

	// Reaching the end is worth acknowledging, so finishing hands off to the
	// completion card instead of just closing like Skip does.
	const finishTutorial = useCallback(() => {
		setCompletedTutorial(activeTutorial);
		setActiveTutorial(null);
		setCurrentStepIndex(0);
	}, [activeTutorial]);

	const dismissCompletion = useCallback(() => {
		setCompletedTutorial(null);
	}, []);

	const goToStep = useCallback((index: number) => {
		setCurrentStepIndex(index);
	}, []);

	const steps = activeTutorial ? TUTORIALS[activeTutorial] : NO_STEPS;

	const value = useMemo<TutorialContextValue>(
		() => ({
			activeTutorial,
			steps,
			currentStepIndex,
			completedTutorial,
			startTutorial,
			exitTutorial,
			finishTutorial,
			dismissCompletion,
			goToStep,
		}),
		[
			activeTutorial,
			steps,
			currentStepIndex,
			completedTutorial,
			startTutorial,
			exitTutorial,
			finishTutorial,
			dismissCompletion,
			goToStep,
		]
	);

	return (
		<TutorialContext.Provider value={value}>
			{children}
		</TutorialContext.Provider>
	);
};
